import React, { useState, useEffect } from 'react';
import { getOnlineUsersCount, getOnlineUsers, getUsersLastSeen } from '../services/api';
import { mqttSupportService } from '../services/mqttSupportService';
import { FiUsers, FiActivity, FiRefreshCw, FiClock } from 'react-icons/fi';
import './OnlineUsers.css';

interface OnlineUser {
  id: number;
  name: string;
  phoneNumber: string;
  role: string;
  lastSeen: string;
  profileImage: string | null;
}

interface UserLastSeen extends OnlineUser {
  isOnline: boolean;
}

const OnlineUsers: React.FC = () => {
  const [viewType, setViewType] = useState<'online' | 'all'>('online');
  const [onlineCount, setOnlineCount] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [allUsers, setAllUsers] = useState<UserLastSeen[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'all' | 'id' | 'name' | 'phone' | 'role'>('all');
  const [mqttConnected, setMqttConnected] = useState(false);

  // Handle online status updates from MQTT
  const handleOnlineStatusUpdate = React.useCallback((statusUpdate: any) => {
    console.log('📡 [OnlineUsers] Received online status update:', statusUpdate);
    
    if (statusUpdate.type === 'USER_ONLINE' || statusUpdate.isOnline === true) {
      // User came online
      setOnlineUsers(prev => {
        // Check if user already exists
        const exists = prev.some(u => u.id === statusUpdate.userId);
        if (exists) {
          // Update existing user
          const updated = prev.map(u => 
            u.id === statusUpdate.userId 
              ? {
                  id: statusUpdate.userId,
                  name: statusUpdate.userName || u.name,
                  phoneNumber: statusUpdate.phoneNumber || u.phoneNumber,
                  role: statusUpdate.userRole || u.role,
                  lastSeen: statusUpdate.timestamp || u.lastSeen,
                  profileImage: null,
                }
              : u
          );
          // Update count based on actual online users
          setOnlineCount(updated.length);
          return updated;
        } else {
          // Add new user
          const updated = [...prev, {
            id: statusUpdate.userId,
            name: statusUpdate.userName || `User ${statusUpdate.userId}`,
            phoneNumber: statusUpdate.phoneNumber || '',
            role: statusUpdate.userRole || 'USER',
            lastSeen: statusUpdate.timestamp || new Date().toISOString(),
            profileImage: null,
          }];
          // Update count based on actual online users
          setOnlineCount(updated.length);
          return updated;
        }
      });
      
      setLastUpdate(new Date());
    } else if (statusUpdate.type === 'USER_OFFLINE' || statusUpdate.isOnline === false) {
      // User went offline
      setOnlineUsers(prev => {
        const updated = prev.filter(u => u.id !== statusUpdate.userId);
        // Update count based on actual online users
        setOnlineCount(updated.length);
        return updated;
      });
      
      setLastUpdate(new Date());
    }
  }, []);

  // Initial load and MQTT setup - runs on component mount and when viewType changes
  useEffect(() => {
    console.log('🔄 [OnlineUsers] Component mounted or viewType changed, fetching data...');
    
    // Step 1: Fetch initial data from API immediately
    loadData();
    
    // Step 2: Connect to MQTT for real-time online status updates
    const token = localStorage.getItem('adminToken');
    if (token) {
      console.log('🔌 [OnlineUsers] Setting up MQTT connection...');
      mqttSupportService.connect(token);
      
      // Register connection status callback
      mqttSupportService.onConnectionStatusChange((connected) => {
        console.log('📡 [OnlineUsers] MQTT connection status:', connected ? 'Connected' : 'Disconnected');
        setMqttConnected(connected);
      });
      
      // Register online status update callback - this will listen while component is mounted
      mqttSupportService.onOnlineStatusUpdate(handleOnlineStatusUpdate);
      
      // Initial connection check
      const checkConnection = setInterval(() => {
        const connected = mqttSupportService.isConnectedToBroker();
        setMqttConnected(connected);
      }, 3000);
      
      // Cleanup: Remove MQTT callbacks when component unmounts
      return () => {
        console.log('🧹 [OnlineUsers] Cleaning up MQTT callbacks...');
        clearInterval(checkConnection);
        // Note: We don't disconnect MQTT here as it might be used by other components
        // The mqttSupportService manages the connection lifecycle
      };
    }
  }, [viewType, handleOnlineStatusUpdate]);

  // Periodic refresh as backup - runs independently
  useEffect(() => {
    console.log('⏰ [OnlineUsers] Setting up periodic refresh (60s interval)...');
    
    // Refresh every 60 seconds as backup
    const interval = setInterval(() => {
      console.log('🔄 [OnlineUsers] Periodic refresh triggered...');
      loadData();
    }, 60000);
    
    return () => {
      console.log('🧹 [OnlineUsers] Clearing periodic refresh interval...');
      clearInterval(interval);
    };
  }, [viewType]);

  const loadData = React.useCallback(async () => {
    try {
      console.log('📥 [OnlineUsers] Loading data for viewType:', viewType);
      setLoading(true);
      
      if (viewType === 'all') {
        // For all users view, get all users data
        const [countData, usersData] = await Promise.all([
          getOnlineUsersCount().catch((err) => {
            console.error('❌ [OnlineUsers] Failed to get online count:', err);
            return { onlineCount: 0, totalUsers: 0 };
          }),
          getUsersLastSeen().catch((err) => {
            console.error('❌ [OnlineUsers] Failed to get users last seen:', err);
            return { users: [] };
          })
        ]);
        
        setTotalUsers(countData.totalUsers || 0);
        setAllUsers(usersData.users || []);
        console.log('✅ [OnlineUsers] Loaded', usersData.users?.length || 0, 'total users');
      } else {
        // For online view, fetch initial online users from API
        const [countData, usersData] = await Promise.all([
          getOnlineUsersCount().catch((err) => {
            console.error('❌ [OnlineUsers] Failed to get online count:', err);
            return { onlineCount: 0, totalUsers: 0 };
          }),
          getOnlineUsers().catch((err) => {
            console.error('❌ [OnlineUsers] Failed to get online users:', err);
            return [];
          })
        ]);
        
        setTotalUsers(countData.totalUsers || 0);
        
        // Handle both array and object response formats
        const onlineUsersList = Array.isArray(usersData) 
          ? usersData 
          : (usersData.onlineUsers || []);
        
        setOnlineUsers(onlineUsersList);
        setOnlineCount(onlineUsersList.length);
        
        console.log('✅ [OnlineUsers] Loaded', onlineUsersList.length, 'online users from API');
      }
      
      setLastUpdate(new Date());
    } catch (error: any) {
      console.error('❌ [OnlineUsers] Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  const formatLastSeen = (lastSeen: string | null): string => {
    if (!lastSeen) return 'Never';
    
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchesSearch = (user: {
    id: number;
    name: string;
    phoneNumber: string;
    role: string;
  }) => {
    if (!normalizedSearch) return true;

    const getFieldValue = () => {
      switch (searchField) {
        case 'id':
          return user.id?.toString() || '';
        case 'name':
          return user.name || '';
        case 'phone':
          return user.phoneNumber || '';
        case 'role':
          return user.role || '';
        default:
          return [
            user.id?.toString() || '',
            user.name || '',
            user.phoneNumber || '',
            user.role || '',
          ].join(' ');
      }
    };

    return getFieldValue().toLowerCase().includes(normalizedSearch);
  };

  const filteredOnlineUsers = onlineUsers.filter(matchesSearch);
  const filteredAllUsers = allUsers.filter(matchesSearch);

  return (
    <div className="online-users">
      <div className="online-header">
        <h2>
          {React.createElement(FiActivity as any, { className: "section-icon" })} 
          Online Users
        </h2>
        <div className="header-actions">
          <div className="search-controls">
            <select
              className="search-select"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value as typeof searchField)}
            >
              <option value="all">All Fields</option>
              <option value="id">User ID</option>
              <option value="name">Name</option>
              <option value="phone">Phone</option>
              <option value="role">Role</option>
            </select>
            <div className="search-box">
            <input
              type="text"
              placeholder={`Search by ${searchField === 'all' ? 'any field' : searchField}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          </div>
          <button onClick={loadData} className="refresh-button" disabled={loading}>
            {React.createElement(FiRefreshCw as any, { className: loading ? 'spinning' : '' })} 
            Refresh
          </button>
          <div className="view-toggle">
            <button
              className={viewType === 'online' ? 'active' : ''}
              onClick={() => setViewType('online')}
            >
              Online Only
            </button>
            <button
              className={viewType === 'all' ? 'active' : ''}
              onClick={() => setViewType('all')}
            >
              All Users
            </button>
          </div>
        </div>
      </div>

      <div className="online-stats">
        <div className="stat-card">
          <div className="stat-icon online">{React.createElement(FiUsers as any)}</div>
          <div className="stat-info">
            <h3>{onlineCount}</h3>
            <p>Online Now</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon total">{React.createElement(FiUsers as any)}</div>
          <div className="stat-info">
            <h3>{totalUsers}</h3>
            <p>Total Users</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            {React.createElement(FiClock as any)}
          </div>
          <div className="stat-info">
            <h3>{lastUpdate.toLocaleTimeString()}</h3>
            <p>Last Update</p>
          </div>
        </div>
      </div>

      {/* <div className="mqtt-logs-section">
        <div className="mqtt-logs-header">
          <div className="mqtt-header-left">
            <h3>Port 8084 Activity Logs</h3>
            <span className={`mqtt-connection-status ${mqttConnected ? 'connected' : 'disconnected'}`}>
              <span className="status-dot-small"></span>
              {mqttConnected ? 'Connected' : 'Disconnected'}
            </span>
            <span className="mqtt-message-count">
              {mqttLogs.length} messages
            </span>
          </div>
          <button 
            className="toggle-logs-button"
            onClick={() => setShowMqttLogs(!showMqttLogs)}
          >
            {showMqttLogs ? 'Hide' : 'Show'} Logs
          </button>
        </div>
        
        {showMqttLogs && (
          <div className="mqtt-logs-container">
            {mqttLogs.length === 0 ? (
              <div className="mqtt-logs-empty-state">
                <p>No messages received yet from Port 8084</p>
                <p>Waiting for Android devices to send online status...</p>
              </div>
            ) : (
              mqttLogs.map((log) => (
                <div key={log.id} className={`mqtt-log-item ${log.isOnline ? 'online' : 'offline'}`}>
                  <div className="log-header">
                    <span className="log-number">#{log.messageNumber}</span>
                    <span className="log-time">{log.timestamp.toLocaleTimeString()}</span>
                    <span className={`log-status ${log.isOnline ? 'status-online' : 'status-offline'}`}>
                      <span className={`status-indicator-text ${log.isOnline ? 'online' : 'offline'}`}>
                        {log.isOnline ? 'ONLINE' : 'OFFLINE'}
                      </span>
                    </span>
                  </div>
                  <div className="log-details">
                    <div className="log-row">
                      <span className="log-label">Topic:</span>
                      <span className="log-value">{log.topic}</span>
                    </div>
                    {log.userId && (
                      <div className="log-row">
                        <span className="log-label">User ID:</span>
                        <span className="log-value">{log.userId}</span>
                      </div>
                    )}
                    <div className="log-row">
                      <span className="log-label">Payload:</span>
                      <pre className="log-payload">{JSON.stringify(log.payload, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div> */}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : (
        <div className="users-list">
          {viewType === 'online' ? (
            filteredOnlineUsers.length === 0 ? (
              <div className="empty-state">
                <p>No users online at the moment</p>
              </div>
            ) : (
              filteredOnlineUsers.map((user) => (
                <div 
                  key={user.id} 
                  className="user-item online"
                >
                  <div className="user-avatar">
                    {user.profileImage ? (
                      <img src={user.profileImage} alt={user.name} />
                    ) : (
                      <div className="avatar-placeholder">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="online-indicator"></span>
                  </div>
                  <div className="user-info">
                    <h4>{user.name}</h4>
                    <p>{user.phoneNumber}</p>
                    <span className="user-role">{user.role}</span>
                  </div>
                  <div className="last-seen">
                    <p>
                      <span className="status-online">Online</span>
                    </p>
                  </div>
                </div>
              ))
            )
          ) : (
            filteredAllUsers.length === 0 ? (
              <div className="empty-state">
                <p>No users found</p>
              </div>
            ) : (
              filteredAllUsers.map((user) => (
                <div key={user.id} className={`user-item ${user.isOnline ? 'online' : 'offline'}`}>
                  <div className="user-avatar">
                    {user.profileImage ? (
                      <img src={user.profileImage} alt={user.name} />
                    ) : (
                      <div className="avatar-placeholder">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {user.isOnline && <span className="online-indicator"></span>}
                  </div>
                  <div className="user-info">
                    <h4>{user.name}</h4>
                    <p>{user.phoneNumber}</p>
                    <span className="user-role">{user.role}</span>
                  </div>
                  <div className="last-seen">
                    <p>
                      {user.isOnline ? (
                        <span className="status-online">Online</span>
                      ) : (
                        <span className="status-offline">
                          Last seen: {formatLastSeen(user.lastSeen)}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))
            )
          )}
        </div>
      )}
    </div>
  );
};

export default OnlineUsers;

